import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import TkStrpper from "../../components/atoms/TkStepper";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import {
  headList,
  noticeMessage,
  schema,
  searchList,
  stepperData,
} from "./parts";
import TkDataTable from "../../components/molecules/TkDataTable";
import { httpIAPI_FAC018 } from "./api/request";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkSearchForm from "../../components/molecules/TkSearchForm";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkButton from "../../components/atoms/TkButton";
import "./_style.scss";
import TkDialog from "../../components/atoms/TkDialog";
import TkFakeTableLine from "../../components/atoms/TkFakeTableLine";

/**
 * SCRC050125
 * このコンポーネントは、TkInfoManual,TkMessageDialog,TkFacilityTableChangerをレンダリングします。
 * @returns JSX.Element SCRC050125_部分解除　施設選択
 */
const SCRC050125: React.FC = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  // 検索
  const [seachedFacilityList, setSearchedFacilityList] = useState<any[]>([]);
  // 施設一覧
  const [facilityList, setFacilityList] = useState<any[]>([]);
  // api施設一覧
  const [apiFacilityList, setApiFacilityList] = useState<any[]>([]);
  // 選択された施設番号
  const facilityNoList = useRef<string[]>([]);
  // 従業状況の更新(ダイアログ)
  const [openNoticeDialog, setOpenNoticeDialog] = useState(true);
  const [checkedListData, setCheckedListData] = useState([]);
  const contractorNo =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
      ? businessState.common?.contractorNo || ""
      : state.authInfo?.contractorNo || "";
  // レスポンス申請ID
  const [resApplyID, setResApplyId] = useState<any>();

  // 部分解除施設選択表示API
  const handleInit = async () => {
    try {
      const res = await httpIAPI_FAC018({
        contractorNo: contractorNo,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        const bodyList = formatBodyList(res.facilityList || []);
        setApiFacilityList(res.facilityList || []);
        setFacilityList(bodyList);
        setSearchedFacilityList(bodyList);
        setResApplyId(res.applyId);
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // テーブルデータ整形
  const formatBodyList = (facilityList: any) => {
    const checkedList: any = [];
    for (let i = 0; i < facilityList.length; i++) {
      switch (facilityList[i].changeabilityFlg) {
        case "1":
          checkedList.push(i);
          break;
      }
    }
    setCheckedListData(checkedList);
    const formatFacilityList = facilityList.map((item: any) => {
      return [
        {
          children: (item.facilityNo ?? "") + ":" + (item.facilityName ?? ""),
          forSort: item.facilityNo,
          disabledCheckBox: item.changeabilityFlg === "1" ? true : false,
        },
        {
          children: item.facilityKbn,
          forSort: item.facilityKbn,
        },
        {
          children: item.facilityKind,
          forSort: item.facilityKind,
        },
        {
          children: item.status,
          forSort: item.status,
        },
        {
          children: item.processStatus,
          forSort: item.processStatus,
        },
        {
          children: item.joinStaffNum,
          forSort: item.joinStaffNum,
        },
      ];
    });
    return formatFacilityList;
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 検索
  const onSearch = (data: any) => {
    let searchedFacilityList = [...seachedFacilityList];
    facilityNoList.current = [];
    // 施設種類
    if (data.facilityType) {
      searchedFacilityList = searchedFacilityList.filter((item: any) => {
        return item[2]?.children.includes(data.facilityType);
      });
    }

    // 施設名
    if (data.facilityName) {
      searchedFacilityList = searchedFacilityList.filter((item: any) => {
        return item[0]?.children.includes(data.facilityName);
      });
    }

    setFacilityList([...searchedFacilityList]);

    // 選択された施設一覧
    const searchedFacilityNo = searchedFacilityList.map((item) => {
      return item?.[0]?.children?.split?.(":")?.[0];
    });
    facilityNoList.current = searchedFacilityNo;
  };

  // 選択
  const handleSelect = (indexArr: number[]) => {
    facilityNoList.current = [];

    if (indexArr.length === 0) {
      return;
    }

    const selectedList = indexArr.map((index) => {
      return facilityList[index];
    });
    const selectedFacilityNo = selectedList.map((item) => {
      return item?.[0]?.children?.split?.(":")?.[0];
    });
    facilityNoList.current = selectedFacilityNo;
  };

  // 次へ
  const handleNext = () => {
    // チェックボックスが空リストの場合、画面遷移できない
    if (!facilityNoList.current?.length) {
      setState({
        ...state,
        errorMsgId: "TKMSG_SCR0044E",
      });
      return;
    }
    const selectedFacilityList = apiFacilityList
      .filter((item) => {
        return facilityNoList.current.includes(item.facilityNo);
      })
      .map((item) => {
        return {
          facilityName: item.facilityName,
          facilityNo: item.facilityNo,
          facilityKbn: item.facilityKbn,
          facilityKind: item.facilityKind,
          reformKbn: item.reformKbn,
        };
      });

    setBusinessState({
      ...businessState,
      job_16: {
        ...businessState.job_16,
        applyId: resApplyID,
        facilityInfoList: selectedFacilityList,
      },
    });

    navigate("/SCRC050126");
  };

  return (
    <Box data-testid="SCRC050125test" className="SCRC050125">
      {!openNoticeDialog && (
        <Box>
          <TkStrpper steps={stepperData} activeStep={0} />
          <TkInfoManual rule="\n">
            {labelids.labelid.LBLC050125_01}
          </TkInfoManual>
          {state.authInfo?.userType ===
            authorityUserType.userTypeNumber.KIKO && (
            <TkFakeTableLine
              type="contractorAndNumber"
              contractorName="契約者（法人）名"
              contractorNumber={`${businessState.common?.contractorNo}:${businessState.common?.contractorName}`}
              data-testid="contractorName"
            />
          )}
          <TkDataTable
            headList={headList}
            wantToCheck={checkedListData}
            bodyList={facilityList}
            checkSection={true}
            onCheckSectionWidth={(indexArr: any) => handleSelect(indexArr)}
            headerLeftComponent={
              <TkSearchForm
                listData={searchList}
                onSearch={onSearch}
                ruleSchema={schema}
              />
            }
          />
          <Box className="buttonGroup">
            <TkBackButton />
            <TkButton onClick={handleNext}>次へ</TkButton>
          </Box>
        </Box>
      )}
      <TkDialog
        open={openNoticeDialog}
        title="　【従業状況の登録確認】"
        onCancel={() => {
          setOpenNoticeDialog(false);
        }}
        onSave={() => {
          navigate("/SCRC050114");
        }}
        okText="従業状況の更新"
        cancelText="次へ"
      >
        <TkInfoManual rule="\n">{noticeMessage}</TkInfoManual>
      </TkDialog>
    </Box>
  );
};
export default SCRC050125;
